import axios from 'axios';
import Cookies from 'js-cookie';
import { CONFIG } from '@/config/Config';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';

export interface SavedFilter {
  id: string;
  filterId?: string;
  name: string;
  filterObj?: Record<string, unknown>;
}

class SavedFiltersService {
  private readonly axiosInstance = axios.create({ timeout: 60000 });

  constructor() {
    this.axiosInstance.interceptors.request.use((config) => {
      const token = Cookies.get(CONFIG.AUTH.SSID);
      const universeId = getUniId();
      const accountId = getAccountId();

      if (token) config.headers[CONFIG.AUTH.SSID] = token;
      if (universeId) config.headers['stilingue-universe-id'] = universeId;
      if (accountId) config.headers['stilingue-account-id'] = accountId;
      return config;
    });
  }

  private buildUrl(path: string): string {
    const gateway = CONFIG.getGatewayUrl();
    const version = CONFIG.getVersion();
    // When running inside warroom (gateway empty), use root-relative URL so the
    // browser routes through the warroom proxy and sends session cookies automatically.
    return gateway
      ? `${gateway}panels-service/${version}${path}`
      : `/panels-service/${version}${path}`;
  }

  async getSavedFilters(): Promise<SavedFilter[]> {
    const account_id = getAccountId();
    const universe_id = getUniId();
    const url = this.buildUrl('/saved-filters');
    const response = await this.axiosInstance.get<unknown>(url, {
      params: { account_id, universe_id },
    });
    const data = response.data;
    const resolveFilterObj = (raw: unknown): Record<string, unknown> | undefined => {
      if (!raw) return undefined;
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw) as Record<string, unknown>;
        } catch (error) {
          console.error('Failed to parse saved filter payload', error);
          return undefined;
        }
      }
      if (typeof raw === 'object') return raw as Record<string, unknown>;
      return undefined;
    };

    // API may return 'filterObj' or 'filter' as the field name; value may be
    // a JSON string or already an object. Non-object entries are skipped.
    const parseFilterObj = (raw: unknown): SavedFilter[] =>
      (raw as unknown[])
        .filter((f): f is Record<string, unknown> => f != null && typeof f === 'object')
        .map((f) => ({ ...f, filterObj: resolveFilterObj(f['filterObj'] ?? f['filter']) } as SavedFilter));
    if (Array.isArray(data)) return parseFilterObj(data);
    if (data != null && typeof data === 'object') {
      const nested = (data as Record<string, unknown>)['data'] ??
        (data as Record<string, unknown>)['message'] ??
        (data as Record<string, unknown>)['filters'] ??
        (data as Record<string, unknown>)['results'];
      if (Array.isArray(nested)) return parseFilterObj(nested);
    }
    return [];
  }

  async createSavedFilter(name: string, filterId: string, filterObj: Record<string, unknown>): Promise<SavedFilter> {
    const account_id = getAccountId();
    const universe_id = getUniId();
    const url = this.buildUrl('/saved-filters');
    const params: Record<string, unknown> = { account_id, universe_id };
    const response = await this.axiosInstance.post<unknown>(url, {
      id: filterId,
      name,
      filter: JSON.stringify(filterObj),
    }, { params });
    const data = response.data;
    if (data && typeof data === 'object') {
      const nested = (data as Record<string, unknown>)['data'] ?? data;
      return nested as SavedFilter;
    }
    return data as SavedFilter;
  }

  async updateSavedFilter(
    id: string,
    editingFilterId: string,
    name: string,
    filterObj: Record<string, unknown>,
  ): Promise<void> {
    const account_id = getAccountId();
    const universe_id = getUniId();
    const url = this.buildUrl('/saved-filters');
    const params: Record<string, unknown> = { account_id, universe_id };

    const filterObjString = filterObj ? JSON.stringify(filterObj) : '{}';

    await this.axiosInstance.put(url, {
      id,
      editingfilterId: editingFilterId, // wrong camel case on the backend, but we have to keep it for now
      name,
      filter: filterObjString,
    }, { params });
  }

  async deleteSavedFilter(filterId: string): Promise<void> {
    const account_id = getAccountId();
    const universe_id = getUniId();
    const url = this.buildUrl(`/saved-filters/${encodeURIComponent(filterId)}`);
    const params: Record<string, unknown> = { account_id, universe_id };
    await this.axiosInstance.delete(url, { params });
  }
}

const savedFiltersService = new SavedFiltersService();
export default savedFiltersService;
